import React, { useEffect, useRef, useState } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle, Search, Phone, Download } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import API, { getKaryawan, createKaryawan, updateKaryawan, deleteKaryawan, getBagian, getUsers } from '../api';
import { useAuth } from '../AuthContext';

const STATUS_OPTIONS = ['Aktif', 'Nonaktif', 'Cuti'];
const JK_OPTIONS = ['Laki-laki', 'Perempuan'];

// ============ MAP PICKER — ambil koordinat radar dengan klik di peta ============
function RadarMapPicker({ target, lat, lng, onPick, onClose }) {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const [pickLat, setPickLat] = useState(lat ? Number(lat) : '');
  const [pickLng, setPickLng] = useState(lng ? Number(lng) : '');
  const [locating, setLocating] = useState(false);

  const placeMarker = (latv, lngv) => {
    const map = leafletMapRef.current;
    if (!map) return;
    setPickLat(Number(latv));
    setPickLng(Number(lngv));
    if (markerRef.current) map.removeLayer(markerRef.current);
    const icon = L.divIcon({
      className: '',
      html: '<div style="width:26px;height:26px;background:#f59e0b;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,0.4)"></div>',
      iconSize: [26, 26],
      iconAnchor: [13, 26],
      popupAnchor: [0, -26],
    });
    markerRef.current = L.marker([Number(latv), Number(lngv)], { icon }).addTo(map);
    // Lingkaran ±500m — area check-in dari titik radar (membantu verifikasi posisi)
    if (circleRef.current) map.removeLayer(circleRef.current);
    circleRef.current = L.circle([Number(latv), Number(lngv)], {
      radius: 500,
      color: '#2dce89',
      weight: 2,
      fillColor: '#2dce89',
      fillOpacity: 0.1,
      dashArray: '6, 4',
    }).addTo(map);
  };

  useEffect(() => {
    const container = mapRef.current;
    if (!container) return;
    const latInit = lat ? Number(lat) : -6.203866;
    const lngInit = lng ? Number(lng) : 106.840231;
    const map = L.map(container, {
      center: [latInit, lngInit],
      zoom: lat ? 16 : 12,
      zoomControl: true,
      attributionControl: false,
    });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { maxZoom: 19 }).addTo(map);
    // Set ref SEBELUM placeMarker awal — jika tidak, marker & lingkaran awal
    // tidak akan digambar (placeMarker membaca ref yang masih null)
    leafletMapRef.current = map;
    map.on('click', e => placeMarker(e.latlng.lat, e.latlng.lng));
    if (lat && lng) placeMarker(Number(lat), Number(lng));
    // Leaflet di container yang baru dirender perlu invalidateSize agar tile muat
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); leafletMapRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) { alert('Geolocation tidak didukung browser ini'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const map = leafletMapRef.current;
        if (map) map.setView([pos.coords.latitude, pos.coords.longitude], 16);
        placeMarker(pos.coords.latitude, pos.coords.longitude);
        setLocating(false);
      },
      err => { setLocating(false); alert('Gagal mengambil lokasi: ' + (err.message || 'izin ditolak')); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const canSave = pickLat !== '' && pickLng !== '';
  const coordText = pickLat !== ''
    ? `Lat: ${Number(pickLat).toFixed(6)}  ·  Lng: ${Number(pickLng).toFixed(6)}`
    : 'Belum ada titik — klik di peta';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <h3>🗺️ Pilih Titik Radar {String(target).replace('radar', '')}</h3>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 10 }}>
            {lat && lng ? <b>Titik radar saat ini sudah ditandai</b> : <b>Klik titik pada peta</b>} — pin oranye = titik radar, <b>lingkaran hijau = area check-in ±500 m</b>.
            {lat && lng ? ' Klik peta untuk menyesuaikan bila perlu.' : ''}
          </div>
          <div ref={mapRef} style={{ width: '100%', height: 320, borderRadius: 10, overflow: 'hidden', zIndex: 0 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={useMyLocation} disabled={locating}
              style={{ fontSize: '0.78rem', padding: '8px 12px' }}>
              {locating ? 'Mencari lokasi...' : '📍 Gunakan Lokasi Saya'}
            </button>
            <div style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#475569', flex: 1, textAlign: 'right' }}>
              {coordText}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Batal</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={() => onPick(Number(pickLat), Number(pickLng))}>
            Simpan Koordinat
          </button>
        </div>
      </div>
    </div>
  );
}

const emptyForm = {
  nik: '', nama: '', tempat_lahir: '', tanggal_lahir: '', jenis_kelamin: '',
  alamat: '', telepon: '', email: '', jabatan: '', bagian_id: '', tanggal_masuk: '', status: 'Aktif', user_id: '',
  // Radar absensi (geofencing) — 5 titik: kosongkan semua → bebas absen di mana saja
  latitude_radar: '', longitude_radar: '', altitude_radar: '',
  latitude_radar2: '', longitude_radar2: '', altitude_radar2: '',
  latitude_radar3: '', longitude_radar3: '', altitude_radar3: '',
  latitude_radar4: '', longitude_radar4: '', altitude_radar4: '',
  latitude_radar5: '', longitude_radar5: '', altitude_radar5: '',
};

export default function Karyawan() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [bagianList, setBagianList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBagian, setFilterBagian] = useState(''); // '' = semua bagian
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mapPicker, setMapPicker] = useState(null); // { target: 'radar1'|'radar2', lat, lng }

  const load = () => {
    setLoading(true);
    Promise.all([getKaryawan(), getBagian(), getUsers()])
      .then(([res, bagRes, usrRes]) => { setData(res.data); setBagianList(bagRes.data); setUserList(usrRes.data || []); })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  // Daftar akun user yang boleh dipilih di form: user yang belum tertaut ke
  // karyawan mana pun + user yang sedang tertaut ke karyawan yang diedit.
  // Akun nonaktif (is_active=false) disembunyikan agar tidak membingungkan.
  const linkedUserIds = new Set(data.filter(d => d.user_id).map(d => d.user_id));
  const userOptions = (userList || []).filter(u =>
    (u.is_active !== false || u.id === Number(form.user_id)) &&
    (!linkedUserIds.has(u.id) || u.id === Number(form.user_id))
  );
  useEffect(() => { load(); }, []);

  const exportCSV = async () => {
    try {
      const params = { export: 'csv' };
      if (search) params.search = search;
      const res = await API.get('/karyawan', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      link.setAttribute('download', `karyawan_${now.toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export CSV gagal', err);
      alert('Gagal mengexport CSV. Silahkan coba lagi.');
    }
  };

  const filtered = data.filter(d => {
    if (search && !d.nama?.toLowerCase().includes(search.toLowerCase()) &&
        !d.nik?.toLowerCase().includes(search.toLowerCase()) &&
        !d.jabatan?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterBagian && Number(d.bagian_id) !== Number(filterBagian)) return false;
    return true;
  });

  const openCreate = () => { setEditing(null); setForm(emptyForm); setError(''); setShowModal(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      nik: item.nik, nama: item.nama, tempat_lahir: item.tempat_lahir || '',
      tanggal_lahir: item.tanggal_lahir ? item.tanggal_lahir.slice(0,10) : '',
      jenis_kelamin: item.jenis_kelamin || '', alamat: item.alamat || '',
      telepon: item.telepon || '', email: item.email || '', jabatan: item.jabatan || '',
      bagian_id: item.bagian_id ?? '', tanggal_masuk: item.tanggal_masuk ? item.tanggal_masuk.slice(0,10) : '',
      status: item.status || 'Aktif', user_id: item.user_id ?? '',
      latitude_radar: item.latitude_radar ?? '', longitude_radar: item.longitude_radar ?? '', altitude_radar: item.altitude_radar ?? '',
      latitude_radar2: item.latitude_radar2 ?? '', longitude_radar2: item.longitude_radar2 ?? '', altitude_radar2: item.altitude_radar2 ?? '',
      latitude_radar3: item.latitude_radar3 ?? '', longitude_radar3: item.longitude_radar3 ?? '', altitude_radar3: item.altitude_radar3 ?? '',
      latitude_radar4: item.latitude_radar4 ?? '', longitude_radar4: item.longitude_radar4 ?? '', altitude_radar4: item.altitude_radar4 ?? '',
      latitude_radar5: item.latitude_radar5 ?? '', longitude_radar5: item.longitude_radar5 ?? '', altitude_radar5: item.altitude_radar5 ?? '',
    });
    setError(''); setShowModal(true);
  };

  const handleSave = async () => {
    setError('');
    if (!form.nik) { setError('NIK harus diisi'); return; }
    if (!form.nama) { setError('Nama harus diisi'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tanggal_lahir: form.tanggal_lahir || null,
        tanggal_masuk: form.tanggal_masuk || null,
        bagian_id: form.bagian_id ? Number(form.bagian_id) : null,
        user_id: form.user_id ? Number(form.user_id) : null,
        latitude_radar: form.latitude_radar !== '' ? Number(form.latitude_radar) : null,
        longitude_radar: form.longitude_radar !== '' ? Number(form.longitude_radar) : null,
        altitude_radar: form.altitude_radar !== '' ? Number(form.altitude_radar) : null,
        latitude_radar2: form.latitude_radar2 !== '' ? Number(form.latitude_radar2) : null,
        longitude_radar2: form.longitude_radar2 !== '' ? Number(form.longitude_radar2) : null,
        altitude_radar2: form.altitude_radar2 !== '' ? Number(form.altitude_radar2) : null,
        latitude_radar3: form.latitude_radar3 !== '' ? Number(form.latitude_radar3) : null,
        longitude_radar3: form.longitude_radar3 !== '' ? Number(form.longitude_radar3) : null,
        altitude_radar3: form.altitude_radar3 !== '' ? Number(form.altitude_radar3) : null,
        latitude_radar4: form.latitude_radar4 !== '' ? Number(form.latitude_radar4) : null,
        longitude_radar4: form.longitude_radar4 !== '' ? Number(form.longitude_radar4) : null,
        altitude_radar4: form.altitude_radar4 !== '' ? Number(form.altitude_radar4) : null,
        latitude_radar5: form.latitude_radar5 !== '' ? Number(form.latitude_radar5) : null,
        longitude_radar5: form.longitude_radar5 !== '' ? Number(form.longitude_radar5) : null,
        altitude_radar5: form.altitude_radar5 !== '' ? Number(form.altitude_radar5) : null,
      };
      if (editing) {
        await updateKaryawan(editing.id, payload);
      } else {
        await createKaryawan(payload);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Yakin ingin menghapus ${item.nama}?`)) return;
    try { await deleteKaryawan(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal menghapus'); }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Data Karyawan</h2>
            <p className="page-desc">{data.length} karyawan terdaftar</p>
          </div>
          {data.length > 0 && (
            <button className="btn btn-secondary" onClick={exportCSV}>
              <Download size={16} /><span>Export CSV</span>
            </button>
          )}
          {hasMenuAction('karyawan', 'create') && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} /><span>Tambah Karyawan</span>
            </button>
          )}
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={18} />
          <input type="text" placeholder="Cari karyawan..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={filterBagian} onChange={e => setFilterBagian(e.target.value)}
          style={{
            padding: '4px 10px', borderRadius: 6, border: '1px solid var(--gray-200)',
            fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer', background: '#fff',
          }}>
          <option value="">Semua Bagian</option>
          {bagianList.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        {filterBagian && (
          <button className="btn btn-secondary"
            style={{ padding: '4px 8px', minHeight: 32, fontSize: '0.7rem', color: 'var(--gray-400)' }}
            onClick={() => setFilterBagian('')}>
            <X size={14} />
          </button>
        )}
      </div>

      {loading ? (
        <div className="page-loading">Memuat data...</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>NIK</th><th>Nama</th><th>Jabatan</th><th>Bagian</th><th>Akun User</th><th>Telepon</th><th>Status</th>
                <th style={{ width: 80 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.id}>
                  <td><strong>{d.nik}</strong></td>
                  <td>{d.nama}</td>
                  <td>{d.jabatan || '-'}</td>
                  <td>{d.bagian_name || '-'}</td>
                  <td>{d.user_username ? <span className="badge badge-default">{d.user_username}</span> : <span className="text-muted">-</span>}</td>
                  <td><span className="cell-with-icon"><Phone size={12} className="text-muted" />{d.telepon || '-'}</span></td>
                  <td><span className={`badge ${d.status === 'Aktif' ? 'badge-success' : d.status === 'Cuti' ? 'badge-default' : 'badge-danger'}`}>{d.status}</span></td>
                  <td>
                    <div className="action-btns">
                      {hasMenuAction('karyawan', 'edit') && (
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(d)}><Edit2 size={14} /></button>
                      )}
                      {hasMenuAction('karyawan', 'delete') && (
                        <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d)}><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} className="text-center text-muted">Tidak ada data</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Karyawan' : 'Tambah Karyawan'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
              <div className="form-grid form-grid-3">
                <div className="form-group">
                  <label>NIK *</label>
                  <input type="text" value={form.nik} onChange={e => setForm({...form, nik: e.target.value})} placeholder="NIK" disabled={!!editing} />
                </div>
                <div className="form-group">
                  <label>Nama Lengkap *</label>
                  <input type="text" value={form.nama} onChange={e => setForm({...form, nama: e.target.value})} placeholder="Nama lengkap" />
                </div>
                <div className="form-group">
                  <label>Jenis Kelamin</label>
                  <select value={form.jenis_kelamin} onChange={e => setForm({...form, jenis_kelamin: e.target.value})}>
                    <option value="">-- Pilih --</option>
                    {JK_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tempat Lahir</label>
                  <input type="text" value={form.tempat_lahir} onChange={e => setForm({...form, tempat_lahir: e.target.value})} placeholder="Tempat lahir" />
                </div>
                <div className="form-group">
                  <label>Tanggal Lahir</label>
                  <input type="date" value={form.tanggal_lahir} onChange={e => setForm({...form, tanggal_lahir: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Jabatan</label>
                  <input type="text" value={form.jabatan} onChange={e => setForm({...form, jabatan: e.target.value})} placeholder="Jabatan" />
                </div>
                <div className="form-group">
                  <label>Bagian</label>
                  <select value={form.bagian_id} onChange={e => setForm({...form, bagian_id: e.target.value})}>
                    <option value="">-- Pilih Bagian --</option>
                    {bagianList.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Akun User (untuk absensi)</label>
                  <select value={form.user_id} onChange={e => setForm({...form, user_id: e.target.value})}>
                    <option value="">-- Pilih Akun --</option>
                    {userOptions.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.full_name || u.username}{u.role ? ` (${u.role})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Telepon</label>
                  <input type="text" value={form.telepon} onChange={e => setForm({...form, telepon: e.target.value})} placeholder="No. telepon" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email" />
                </div>
                <div className="form-group">
                  <label>Tanggal Masuk</label>
                  <input type="date" value={form.tanggal_masuk} onChange={e => setForm({...form, tanggal_masuk: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                    {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group" style={{marginTop: 16}}>
                <label>Alamat</label>
                <textarea rows={2} value={form.alamat} onChange={e => setForm({...form, alamat: e.target.value})} placeholder="Alamat lengkap" />
              </div>
              <div style={{
                marginTop: 18, padding: '12px 14px', borderRadius: 10,
                background: '#f8fafc', border: '1px solid #e2e8f0',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 13 }}>📡</span>
                  <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#334155' }}>Pengaturan Radar Absensi (Geofencing)</span>
                </div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 10 }}>
                  Isi titik koordinat lokasi kerja agar karyawan <b>hanya bisa check-in dalam radius ±500 meter</b> dari salah satu titik.
                  Kosongkan Latitude &amp; Longitude di kelima titik → bebas absen di mana saja.
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '6px 0 2px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.78rem', color: '#475569' }}>📍 Titik Radar 1</div>
                  <button type="button" className="btn btn-secondary"
                    disabled={form.latitude_radar === '' || form.longitude_radar === ''}
                    title={form.latitude_radar === '' || form.longitude_radar === '' ? 'Isi Latitude & Longitude terlebih dahulu' : 'Lihat posisi titik radar di peta'}
                    style={{ fontSize: '0.7rem', padding: '3px 10px' }}
                    onClick={() => setMapPicker({
                      target: 'radar1',
                      lat: form.latitude_radar !== '' ? Number(form.latitude_radar) : null,
                      lng: form.longitude_radar !== '' ? Number(form.longitude_radar) : null,
                    })}>
                    👁️ Cek di Peta
                  </button>
                </div>
                <div className="form-grid form-grid-3">
                  <div className="form-group">
                    <label>Latitude</label>
                    <input type="number" step="any" value={form.latitude_radar}
                      onChange={e => setForm({...form, latitude_radar: e.target.value})}
                      placeholder="cth: -6.203866" />
                  </div>
                  <div className="form-group">
                    <label>Longitude</label>
                    <input type="number" step="any" value={form.longitude_radar}
                      onChange={e => setForm({...form, longitude_radar: e.target.value})}
                      placeholder="cth: 106.840231" />
                  </div>
                  <div className="form-group">
                    <label>Altitude (meter, opsional)</label>
                    <input type="number" step="any" value={form.altitude_radar}
                      onChange={e => setForm({...form, altitude_radar: e.target.value})}
                      placeholder="cth: 25" />
                  </div>
                </div>
                <button type="button" className="btn btn-secondary"
                  style={{ marginTop: 8, fontSize: '0.75rem', padding: '6px 12px' }}
                  onClick={() => setMapPicker({
                    target: 'radar1',
                    lat: form.latitude_radar !== '' ? Number(form.latitude_radar) : null,
                    lng: form.longitude_radar !== '' ? Number(form.longitude_radar) : null,
                  })}>
                  🗺️ Pilih dari Peta (klik titik di map)
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0 2px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.78rem', color: '#475569' }}>📍 Titik Radar 2 (opsional)</div>
                  <button type="button" className="btn btn-secondary"
                    disabled={form.latitude_radar2 === '' || form.longitude_radar2 === ''}
                    title={form.latitude_radar2 === '' || form.longitude_radar2 === '' ? 'Isi Latitude & Longitude terlebih dahulu' : 'Lihat posisi titik radar di peta'}
                    style={{ fontSize: '0.7rem', padding: '3px 10px' }}
                    onClick={() => setMapPicker({
                      target: 'radar2',
                      lat: form.latitude_radar2 !== '' ? Number(form.latitude_radar2) : null,
                      lng: form.longitude_radar2 !== '' ? Number(form.longitude_radar2) : null,
                    })}>
                    👁️ Cek di Peta
                  </button>
                </div>
                <div className="form-grid form-grid-3">
                  <div className="form-group">
                    <label>Latitude</label>
                    <input type="number" step="any" value={form.latitude_radar2}
                      onChange={e => setForm({...form, latitude_radar2: e.target.value})}
                      placeholder="cth: -6.211545" />
                  </div>
                  <div className="form-group">
                    <label>Longitude</label>
                    <input type="number" step="any" value={form.longitude_radar2}
                      onChange={e => setForm({...form, longitude_radar2: e.target.value})}
                      placeholder="cth: 106.845096" />
                  </div>
                  <div className="form-group">
                    <label>Altitude (meter, opsional)</label>
                    <input type="number" step="any" value={form.altitude_radar2}
                      onChange={e => setForm({...form, altitude_radar2: e.target.value})}
                      placeholder="cth: 25" />
                  </div>
                </div>
                <button type="button" className="btn btn-secondary"
                  style={{ marginTop: 8, fontSize: '0.75rem', padding: '6px 12px' }}
                  onClick={() => setMapPicker({
                    target: 'radar2',
                    lat: form.latitude_radar2 !== '' ? Number(form.latitude_radar2) : null,
                    lng: form.longitude_radar2 !== '' ? Number(form.longitude_radar2) : null,
                  })}>
                  🗺️ Pilih dari Peta (klik titik di map)
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0 2px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.78rem', color: '#475569' }}>📍 Titik Radar 3 (opsional)</div>
                  <button type="button" className="btn btn-secondary"
                    disabled={form.latitude_radar3 === '' || form.longitude_radar3 === ''}
                    title={form.latitude_radar3 === '' || form.longitude_radar3 === '' ? 'Isi Latitude & Longitude terlebih dahulu' : 'Lihat posisi titik radar di peta'}
                    style={{ fontSize: '0.7rem', padding: '3px 10px' }}
                    onClick={() => setMapPicker({
                      target: 'radar3',
                      lat: form.latitude_radar3 !== '' ? Number(form.latitude_radar3) : null,
                      lng: form.longitude_radar3 !== '' ? Number(form.longitude_radar3) : null,
                    })}>
                    👁️ Cek di Peta
                  </button>
                </div>
                <div className="form-grid form-grid-3">
                  <div className="form-group">
                    <label>Latitude</label>
                    <input type="number" step="any" value={form.latitude_radar3}
                      onChange={e => setForm({...form, latitude_radar3: e.target.value})}
                      placeholder="cth: -6.211545" />
                  </div>
                  <div className="form-group">
                    <label>Longitude</label>
                    <input type="number" step="any" value={form.longitude_radar3}
                      onChange={e => setForm({...form, longitude_radar3: e.target.value})}
                      placeholder="cth: 106.845096" />
                  </div>
                  <div className="form-group">
                    <label>Altitude (meter, opsional)</label>
                    <input type="number" step="any" value={form.altitude_radar3}
                      onChange={e => setForm({...form, altitude_radar3: e.target.value})}
                      placeholder="cth: 25" />
                  </div>
                </div>
                <button type="button" className="btn btn-secondary"
                  style={{ marginTop: 8, fontSize: '0.75rem', padding: '6px 12px' }}
                  onClick={() => setMapPicker({
                    target: 'radar3',
                    lat: form.latitude_radar3 !== '' ? Number(form.latitude_radar3) : null,
                    lng: form.longitude_radar3 !== '' ? Number(form.longitude_radar3) : null,
                  })}>
                  🗺️ Pilih dari Peta (klik titik di map)
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0 2px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.78rem', color: '#475569' }}>📍 Titik Radar 4 (opsional)</div>
                  <button type="button" className="btn btn-secondary"
                    disabled={form.latitude_radar4 === '' || form.longitude_radar4 === ''}
                    title={form.latitude_radar4 === '' || form.longitude_radar4 === '' ? 'Isi Latitude & Longitude terlebih dahulu' : 'Lihat posisi titik radar di peta'}
                    style={{ fontSize: '0.7rem', padding: '3px 10px' }}
                    onClick={() => setMapPicker({
                      target: 'radar4',
                      lat: form.latitude_radar4 !== '' ? Number(form.latitude_radar4) : null,
                      lng: form.longitude_radar4 !== '' ? Number(form.longitude_radar4) : null,
                    })}>
                    👁️ Cek di Peta
                  </button>
                </div>
                <div className="form-grid form-grid-3">
                  <div className="form-group">
                    <label>Latitude</label>
                    <input type="number" step="any" value={form.latitude_radar4}
                      onChange={e => setForm({...form, latitude_radar4: e.target.value})}
                      placeholder="cth: -6.211545" />
                  </div>
                  <div className="form-group">
                    <label>Longitude</label>
                    <input type="number" step="any" value={form.longitude_radar4}
                      onChange={e => setForm({...form, longitude_radar4: e.target.value})}
                      placeholder="cth: 106.845096" />
                  </div>
                  <div className="form-group">
                    <label>Altitude (meter, opsional)</label>
                    <input type="number" step="any" value={form.altitude_radar4}
                      onChange={e => setForm({...form, altitude_radar4: e.target.value})}
                      placeholder="cth: 25" />
                  </div>
                </div>
                <button type="button" className="btn btn-secondary"
                  style={{ marginTop: 8, fontSize: '0.75rem', padding: '6px 12px' }}
                  onClick={() => setMapPicker({
                    target: 'radar4',
                    lat: form.latitude_radar4 !== '' ? Number(form.latitude_radar4) : null,
                    lng: form.longitude_radar4 !== '' ? Number(form.longitude_radar4) : null,
                  })}>
                  🗺️ Pilih dari Peta (klik titik di map)
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0 2px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.78rem', color: '#475569' }}>📍 Titik Radar 5 (opsional)</div>
                  <button type="button" className="btn btn-secondary"
                    disabled={form.latitude_radar5 === '' || form.longitude_radar5 === ''}
                    title={form.latitude_radar5 === '' || form.longitude_radar5 === '' ? 'Isi Latitude & Longitude terlebih dahulu' : 'Lihat posisi titik radar di peta'}
                    style={{ fontSize: '0.7rem', padding: '3px 10px' }}
                    onClick={() => setMapPicker({
                      target: 'radar5',
                      lat: form.latitude_radar5 !== '' ? Number(form.latitude_radar5) : null,
                      lng: form.longitude_radar5 !== '' ? Number(form.longitude_radar5) : null,
                    })}>
                    👁️ Cek di Peta
                  </button>
                </div>
                <div className="form-grid form-grid-3">
                  <div className="form-group">
                    <label>Latitude</label>
                    <input type="number" step="any" value={form.latitude_radar5}
                      onChange={e => setForm({...form, latitude_radar5: e.target.value})}
                      placeholder="cth: -6.211545" />
                  </div>
                  <div className="form-group">
                    <label>Longitude</label>
                    <input type="number" step="any" value={form.longitude_radar5}
                      onChange={e => setForm({...form, longitude_radar5: e.target.value})}
                      placeholder="cth: 106.845096" />
                  </div>
                  <div className="form-group">
                    <label>Altitude (meter, opsional)</label>
                    <input type="number" step="any" value={form.altitude_radar5}
                      onChange={e => setForm({...form, altitude_radar5: e.target.value})}
                      placeholder="cth: 25" />
                  </div>
                </div>
                <button type="button" className="btn btn-secondary"
                  style={{ marginTop: 8, fontSize: '0.75rem', padding: '6px 12px' }}
                  onClick={() => setMapPicker({
                    target: 'radar5',
                    lat: form.latitude_radar5 !== '' ? Number(form.latitude_radar5) : null,
                    lng: form.longitude_radar5 !== '' ? Number(form.longitude_radar5) : null,
                  })}>
                  🗺️ Pilih dari Peta (klik titik di map)
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Pilih Titik Radar dari Peta */}
      {mapPicker && (
        <RadarMapPicker
          target={mapPicker.target}
          lat={mapPicker.lat}
          lng={mapPicker.lng}
          onPick={(lat, lng) => {
            const n = String(mapPicker.target).replace('radar', '');
            const keyLat = `latitude_radar${n === '1' ? '' : n}`;
            const keyLng = `longitude_radar${n === '1' ? '' : n}`;
            setForm({ ...form, [keyLat]: String(lat), [keyLng]: String(lng) });
            setMapPicker(null);
          }}
          onClose={() => setMapPicker(null)}
        />
      )}
    </div>
  );
}
